import type { Transition } from "motion/react";
import { useAnimation } from "motion/react";
import * as m from "motion/react-m";
import { forwardRef, useImperativeHandle } from "react";

import { cn } from "@/shared/lib";
import type { AnimatedIconProps } from "@/shared/ui/animated-icon";

export interface CopyIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

const DEFAULT_TRANSITION: Transition = {
  type: "spring",
  stiffness: 160,
  damping: 17,
  mass: 1,
};

const CopyIcon = forwardRef<CopyIconHandle, AnimatedIconProps>(
  ({ className, size = 24, ...props }, ref) => {
    const controls = useAnimation();
    useImperativeHandle(ref, () => {
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    return (
      <svg
        className={cn("lucide", className)}
        {...props}
        fill="none"
        height={size}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <m.rect
          animate={controls}
          height="14"
          rx="2"
          ry="2"
          transition={DEFAULT_TRANSITION}
          variants={{
            normal: { translateY: 0, translateX: 0 },
            animate: { translateY: -3, translateX: -3 },
          }}
          width="14"
          x="8"
          y="8"
        />
        <m.path
          animate={controls}
          d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"
          transition={DEFAULT_TRANSITION}
          variants={{
            normal: { x: 0, y: 0 },
            animate: { x: 3, y: 3 },
          }}
        />
      </svg>
    );
  }
);

CopyIcon.displayName = "CopyIcon";

export { CopyIcon };
